/*@flow*/

import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";

import { normalizeQuery } from "../utils/AutosuggestUtil";

import ArticleSearchClient from "../clients/ArticleSearchClient";
import SearchAction from "../actions/SearchAction";
import { getContextDependentAdditionalFilters } from "../utils/SearchUtil";
import ArticleStore from "./ArticleStore";
import { Store } from "shared/src/stores/Store";
import { getBundleToShow } from "../utils/BundleSelectionUtil";
import { determineSearchProfile, determineSuggestProfile } from "../utils/determineSearchProfile";

import { Location, ImageUrls, Bundle } from "shared/src/types/articlesearch/Article";
import { ArticleSuggestionProperties, ArticleSuggestions } from "../datatypes/ArticleSuggestions";
import { getDetailLink } from "shared/src/utils/articleUtil";
import { dontShowInfoSuggestions, suggestionsDebug } from "../searchdiscover/toggles";

export type FavouriteData = {
    bettyVariantId: string;
    bettyBundleId: string;
    imageUrls: ImageUrls;
    description: string;
    location: Location;
};

// on page change we have to deactivate the component, because canceling the fetch promise is not an option.
let isActive = false;
let suggestionsMap: {
    [key: string]: ArticleSuggestions;
} = {};
let favouritesMap: {
    [key: string]: Array<string>;
} = {};
let favouriteDataMap: {
    [key: string]: FavouriteData;
} = {};

class ArticleSuggestionStoreImpl extends Store {
    constructor() {
        super();
        // FIXME(FD) get rid of actions
        this.listenTo(SearchAction.fetchSuggestions, this.onFetchSuggestions as any);
        this.listenTo(SearchAction.fetchFavourites, this.onFetchFavourites as any);
        this.listenTo(SearchAction.cancelSuggestions, this.onCancelSuggestions);
        this.listenTo(SessionStore, this.onSessionStoreUpdate);
        this.listenTo(ArticleStore, this.onArticleStoreUpdate);
    }

    onSessionStoreUpdate() {
        this.resetSuggestions();
    }

    onArticleStoreUpdate() {
        const store = SessionStore.getCustomerStore();

        let changed = false;
        for (const query in favouritesMap) {
            const variantIds = favouritesMap[query];
            for (const variantId of variantIds) {
                const article = ArticleStore.getOneArticleData(variantId);

                if (!article || "state" in article || !article.bundles || variantId in favouriteDataMap) {
                    continue;
                }
                // Have tried to convert getBundleToShow to using the new type which is Record<string, BettyBundle>,
                // however it cause every other functions in that file to change as well. So cast this to { [x: string]: Bundle } for now.
                const bundle = getBundleToShow(article.bundles as { [x: string]: Bundle }, store, null, query);
                if (!bundle) {
                    continue;
                }

                const location = getDetailLink(bundle, null);

                const imageUrls = {
                    bundle: bundle.imageUrl,
                    bundleFallBack: bundle.fallbackImageUrl,
                    variant: article.imageUrl
                };

                favouriteDataMap[variantId] = {
                    bettyVariantId: variantId,
                    bettyBundleId: bundle.bettyBundleId.bettyBundleId,
                    description: bundle.description,
                    imageUrls,
                    location
                };

                changed = true;
            }
        }
        if (changed) {
            this.trigger();
        }
    }

    onFetchSuggestions(query: string) {
        if (query) {
            isActive = true;
            this.loadSuggestions(query);
        }
        this.trigger();
    }
    onCancelSuggestions() {
        isActive = false;
        this.trigger();
    }

    isRelevant(query: string): boolean {
        const containsNonNumber = /[^0-9]+/;
        return query.length >= 1 && containsNonNumber.test(query);
    }

    isCached(query: string): boolean {
        return suggestionsMap[query] !== undefined;
    }

    reduceSuggestionsResponse(response: ArticleSuggestions): ArticleSuggestions {
        let combinedIndices: number[];
        const properties: ArticleSuggestionProperties[] | undefined | null = response.suggestionProperties;
        if (properties) {
            // Partition suggestions into category suggestions and other suggestions.
            const categoryIndices: number[] = [];
            const otherIndices: number[] = [];
            for (const [i, s] of properties.entries()) {
                (s.isCategory ? categoryIndices : otherIndices).push(i);
            }
            // Take up to three category suggestions and fill up to five with
            // other suggestions, but at most three other suggestions.
            combinedIndices = categoryIndices
                .slice(0, 3)
                .concat(otherIndices.slice(0, Math.min(3, 5 - Math.min(3, categoryIndices.length))));
        } else {
            // If no way to determine which are category suggestions, just take first three.
            combinedIndices = [...Array(3).keys()];
        }
        const result = {
            ...response,
            suggestions: combinedIndices.map((i) => response.suggestions[i])
        };
        if (properties) {
            result.suggestionProperties = combinedIndices.map((i) => properties[i]);
        }
        // The typescript compiler will complain, if this is directly in the if clause.
        const explanations: Object[] | undefined | null = response.explanations;
        if (explanations) {
            result.explanations = combinedIndices.map((i) => explanations[i]);
        }
        return result;
    }

    async loadSuggestions(query: string) {
        const q = normalizeQuery(query);
        const store = SessionStore.getCustomerStore();
        if (this.isRelevant(q) && !this.isCached(q) && store) {
            const request = {
                locale: SessionStore.getLocale(),
                country: SessionStore.getCountry(),
                storeId: store,
                customerId: SessionStore.getCustomerId(),
                query: q,
                amount: CountryUtil.getOptionalConfig()?.suggestMoreCategories ? 9 : 3,
                suggestionsType: dontShowInfoSuggestions() ? "searchquery" : null,
                profile: determineSuggestProfile(),
                debugQuery: suggestionsDebug() ? true : null
            };
            let response: ArticleSuggestions;
            try {
                response = await ArticleSearchClient.getSuggestions(request);
            } catch (e) {
                // Nothing to do except trigger.
                this.trigger();
                return;
            }
            if (CountryUtil.getOptionalConfig()?.suggestMoreCategories) {
                // We requested more suggestions than usual, so reduce it again.
                response = this.reduceSuggestionsResponse(response);
            }
            suggestionsMap[response.query] = response;
            this.trigger();
        }
    }

    getSuggestionsMap(): {
        [x: string]: ArticleSuggestions;
    } {
        return isActive ? suggestionsMap : {};
    }

    resetSuggestions() {
        suggestionsMap = {};
        favouritesMap = {};
        favouriteDataMap = {};
        this.trigger();
    }

    onFetchFavourites(query: string) {
        if (query) {
            isActive = true;
            this.loadFavourites(query);
        }
    }

    async loadFavourites(query: string) {
        const storeId = SessionStore.getCustomerStore();
        const customerId = SessionStore.getCustomerId();

        if (query.length >= 2 && favouritesMap[query] === undefined && storeId) {
            const filters = getContextDependentAdditionalFilters();
            const request = {
                language: SessionStore.getLocale(),
                country: SessionStore.getCountry(),
                customerId,
                storeId,
                categories: false,
                facets: false,
                filter: filters,
                profile: determineSearchProfile("searchSuggest"),
                query: query,
                rows: 3,
                page: 1
            };
            try {
                const response = await ArticleSearchClient.doSearch(request);
                favouritesMap[response.query] = response.resultIds;
                // make sure to get the article data if we already have it
                this.onArticleStoreUpdate();
                ArticleStore.fetchArticle(response.resultIds);
                this.trigger();
            } catch (e) {
                // do nothing, we don't want to bother the user about this
            }
        }
    }

    getFavouritesMap(): {
        [x: string]: Array<string>;
    } {
        return isActive ? favouritesMap : {};
    }

    getFavouriteDataMap(): {
        [x: string]: FavouriteData;
    } {
        return favouriteDataMap;
    }
}

export default new ArticleSuggestionStoreImpl();
